import { first } from 'lodash';

import { model } from '@gosupersimple/types';

import { createBasePipeline, createPipelineWithParent } from '@/core/pipeline';
import { generateCellId, isCellWithPipeline, isRecordsLikeCell } from '@/core/cell';
import {
  addExplorationCellsAt,
  addExplorationCells,
  getExplorationCellIndex,
  getExplorationCell,
} from '@/core/exploration';

import { notNil } from '@/lib/utils';

import { PipelineStateContext } from '@/explore/pipeline/state';

import { CombineStepState, CombineStep } from './types';
import { updateCellPipeline } from '../../edit-pipeline/utils';
import {
  Cell,
  Exploration,
  Field,
  JoinPipelineOperation,
  Pipeline,
  RecordsCell,
} from '../../types';

export const getDefaultJoinKey = (fields: Field[], otherItem?: CombineStepState) =>
  fields.find((field) => field.key === otherItem?.joinKey)?.key ??
  fields.find((field) => field.pk ?? false)?.key ??
  first(fields)?.key ??
  '';

export const addCombineToExploration = (
  initialExploration: Exploration,
  items: CombineStep[],
  cellId: string,
): { cell: Cell; exploration: Exploration } | undefined => {
  const result = combinePipeline(initialExploration, items, cellId);

  if (result === undefined) {
    return;
  }

  const { pipeline, exploration } = result;
  const addedCellCount = exploration.view.cells.length - initialExploration.view.cells.length;

  const cell: RecordsCell = {
    id: generateCellId(),
    title: 'Combined data',
    kind: 'records',
    pipeline,
  };

  return {
    cell,
    exploration: addExplorationCellsAt(
      exploration,
      [cell],
      getExplorationCellIndex(result.exploration, cellId) + addedCellCount + 1,
    ),
  };
};

export const updateCombineInExploration = (
  exploration: Exploration,
  items: CombineStep[],
  cellId: string,
  ctx: PipelineStateContext,
): Exploration | undefined => {
  const result = combinePipeline(exploration, items, cellId);

  if (result === undefined) {
    return;
  }

  const cell = getExplorationCell(exploration, cellId);
  const resultPipeline =
    cell !== undefined && isCellWithPipeline(cell)
      ? {
          ...result.pipeline,
          operations: [...result.pipeline.operations, ...cell.pipeline.operations.slice(1)],
        }
      : result.pipeline;

  return updateCellPipeline(result.exploration, cellId, resultPipeline, ctx);
};

// TODO: Reuse part of previous pipeline when updating existing one
const combinePipeline = (initialExploration: Exploration, items: CombineStep[], cellId: string) => {
  const [left, ...rest] = items;

  let exploration: Exploration = initialExploration;
  let pipeline: Pipeline | undefined = undefined;

  const operation: JoinPipelineOperation = {
    operation: 'joinPipeline',
    parameters: {
      steps: rest
        .map((right) => {
          if (right.kind === 'cell') {
            const isFromCurrentExploration =
              getExplorationCell(exploration, right.cellId) !== undefined;

            if (!isFromCurrentExploration) {
              exploration = addExplorationCells(exploration, right.cells, cellId);
            }

            const cell = getRecordsLikeCell(right.cellId, exploration);
            if ('pipelineId' in cell.pipeline && cell.pipeline.pipelineId !== undefined) {
              return {
                stepId: right.stepId,
                pipeline: createPipelineWithParent({
                  parentId: cell.pipeline.pipelineId,
                  operations: [],
                }),
                joinStrategy: { joinKeyOnBase: left.key, joinKeyOnRelated: right.key },
              };
            }
          }

          if (right.kind === 'model') {
            return {
              stepId: right.stepId,
              pipeline: createBasePipeline({ baseModelId: right.modelId, operations: [] }),
              joinStrategy: { joinKeyOnBase: left.key, joinKeyOnRelated: right.key },
            };
          }
        })
        .filter(notNil),
    },
  };

  if (left.kind === 'cell') {
    const isFromCurrentExploration = getExplorationCell(exploration, left.cellId) !== undefined;
    if (!isFromCurrentExploration) {
      exploration = addExplorationCells(exploration, left.cells, cellId);
    }
    const cell = getRecordsLikeCell(left.cellId, exploration);

    if ('pipelineId' in cell.pipeline && cell.pipeline.pipelineId !== undefined) {
      pipeline = createPipelineWithParent({
        parentId: cell.pipeline.pipelineId,
        operations: [operation],
      });
    }
  }

  if (left.kind === 'model') {
    pipeline = createBasePipeline({
      baseModelId: left.modelId,
      operations: [operation],
    });
  }

  if (pipeline !== undefined) {
    return { pipeline, exploration };
  }
};

const getRecordsLikeCell = (cellId: string, exploration: Exploration) => {
  const cell = getExplorationCell(exploration, cellId);
  if (cell === undefined || !isRecordsLikeCell(cell)) {
    throw new Error(`Cell ${cellId} not found or is not RecordsCell`);
  }
  return cell;
};

const JoinableStringTypes = ['String', 'Enum'];
const JoinableNumberTypes = ['Number', 'Integer', 'Float'];

function isJoinTypeCompatible(left: model.PropertyType | null, right: model.PropertyType | null) {
  if (JoinableStringTypes.includes(left ?? '') && JoinableStringTypes.includes(right ?? '')) {
    return true;
  }
  if (JoinableNumberTypes.includes(left ?? '') && JoinableNumberTypes.includes(right ?? '')) {
    return true;
  }
  return left === right;
}

export function pickJoinKeyForStep(previous?: CombineStepState, step?: CombineStepState) {
  if (previous === undefined || step === undefined) {
    return step;
  }

  return {
    ...step,
    joinKey: pickFieldsForStep(previous, step)
      .map((f) => f.key)
      .includes(step.joinKey)
      ? step.joinKey
      : getDefaultJoinKey(step.fields, previous),
  };
}

export function pickFieldsForStep(previous: CombineStepState | undefined, step: CombineStepState) {
  if (previous === undefined) {
    return step.fields;
  }

  const fieldType = previous.fields.find(({ key }) => key === previous.joinKey)?.type;

  return step.fields.filter((field) =>
    fieldType === undefined ? true : isJoinTypeCompatible(fieldType, field.type),
  );
}

export function convertItemToStep(item: CombineStepState | undefined): CombineStep | undefined {
  if (item === undefined) {
    return;
  }

  if (item.kind === 'model') {
    return { stepId: item.stepId, kind: 'model', modelId: item.modelId, key: item.joinKey };
  }

  return {
    stepId: item.stepId,
    kind: 'cell',
    cells: item.cells,
    cellId: item.cellId,
    key: item.joinKey,
  };
}
